import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from '../components/AppIcon';

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication and specific roles
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} props.allowedRoles - Array of roles allowed to access this route
 * @param {string} props.redirectTo - Path to redirect if unauthorized (default: '/login')
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  redirectTo = '/login' 
}) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check if user profile exists and is active
  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-card border border-border p-8 text-center">
          <div className="w-16 h-16 bg-error-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-error" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
            Profile Not Found
          </h2>
          <p className="text-text-secondary mb-6">
            Your user profile could not be loaded. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Check if account is active
  if (profile?.status !== 'active') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-card border border-border p-8 text-center">
          <div className="w-16 h-16 bg-warning-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" size={32} className="text-warning" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
            Account {profile?.status === 'suspended' ? 'Suspended' : 'Inactive'}
          </h2>
          <p className="text-text-secondary mb-6">
            {profile?.status === 'suspended' ?'Your account has been suspended. Please contact support for assistance.'
              : profile?.status === 'pending' ?'Your account is pending approval. You will be notified once approved.' :'Your account is inactive. Please contact support to reactivate.'}
          </p>
        </div>
      </div>
    );
  }

  // Check role-based access if allowedRoles specified
  if (allowedRoles?.length > 0 && !allowedRoles?.includes(profile?.role)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-card border border-border p-8 text-center">
          <div className="w-16 h-16 bg-error-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldAlert" size={32} className="text-error" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
            Access Denied
          </h2>
          <p className="text-text-secondary mb-6">
            You do not have permission to access this page. Your role: <strong>{profile?.role}</strong>
          </p>
          <button
            onClick={() => window.history?.back()}
            className="bg-primary text-white py-2 px-6 rounded-lg font-medium hover:bg-primary-700 transition-smooth"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated, has active profile, and has required role
  return children;
};

export default ProtectedRoute;